import { Route } from 'src/graphql/types/delivery';
import {
  GProviderDispatchStatus,
  GThirdPartyProvider,
} from 'src/graphql/types/generated';
import { naturalSort } from 'src/utils';
import { formatDateTo12Hour } from 'src/utils/date';
import { providerDispatchStatusLabel } from 'src/utils/providerDispatchStatus';
import { isProviderRoute, quoteTotalCents } from 'src/utils/thirdPartyDelivery';
import { formatDollars } from 'src/components/InvoiceByDelivery/billing';

// What the Burq sub-tab and the route panel derive from a route's dispatch and
// its stops' providerDelivery rows. Stop statuses are Burq's own strings,
// verbatim (ProviderDelivery.status); the lists below follow LDS
// (ThirdPartyStopDelivery) and Burq's status model.

// No later event changes the delivery.
export const BURQ_TERMINAL_STATUSES: readonly string[] = [
  'delivered',
  'failed',
  'returned',
];

// The goods have left the warehouse: pickup_complete and every later status.
export const BURQ_PAST_PICKUP_STATUSES: readonly string[] = [
  'pickup_complete',
  'enroute_dropoff',
  'arrived_at_dropoff',
  'dropoff_complete',
  'enroute_to_return',
  'disputed',
  ...BURQ_TERMINAL_STATUSES,
];

const PRE_DISPATCH: readonly GProviderDispatchStatus[] = [
  GProviderDispatchStatus.Quoting,
  GProviderDispatchStatus.QuoteFailed,
  GProviderDispatchStatus.Quoted,
];

const DISPATCHED: readonly GProviderDispatchStatus[] = [
  GProviderDispatchStatus.Dispatched,
  GProviderDispatchStatus.InProgress,
  GProviderDispatchStatus.Completed,
];

// LDS refuses stop moves on and off a route while its push runs and once it
// is dispatched; before dispatch the route is flexible.
const LOCKED: readonly GProviderDispatchStatus[] = [
  GProviderDispatchStatus.Pushing,
  ...DISPATCHED,
];

export type ProviderDeliveryRow = {
  status?: string | null;
  courierAssigned: boolean;
  courierName?: string | null;
  dropoffEta?: string | null;
  trackingUrl?: string | null;
  proofUrls: string[];
  needsAttention: boolean;
  needsAttentionReason?: string | null;
};

export type BurqStop = {
  id: string;
  providerDelivery?: ProviderDeliveryRow | null;
};

export enum BurqCardState {
  Sending = 'Sending',
  CourierNotChosen = 'Courier not chosen',
  // A GM asked Burq to cancel the route and Burq has not answered.
  Canceling = 'Canceling',
  WaitingForDriver = 'Dispatched',
  DriverAssigned = 'Driver assigned',
  OutForDelivery = 'Out for delivery',
  Delivered = 'Delivered',
  Done = 'Done',
}

// A GM's cancel is out and the provider has not answered. Only a running
// dispatch can be canceled, and LDS never clears the claim when a route
// completes, so a finished route with a leftover claim is not canceling.
export const isCancelPending = (
  dispatch:
    | { status: GProviderDispatchStatus; cancelRequestedAt?: unknown }
    | null
    | undefined,
): boolean =>
  dispatch?.status === GProviderDispatchStatus.InProgress &&
  Boolean(dispatch.cancelRequestedAt);

export const isBurqRoute = (route: Route): boolean =>
  isProviderRoute(route) &&
  route.thirdPartyProvider === GThirdPartyProvider.Burq;

// Every delivery and redelivery stop, with or without a row: a stop Burq has
// no order for yet is not settled, so a dispatched route with no rows never
// reads Delivered.
export const burqStopsOf = (route: Route): BurqStop[] => [
  ...(route.stops ?? []).flatMap((stop) =>
    stop.__typename === 'Delivery'
      ? [{ id: stop.id, providerDelivery: stop.providerDelivery }]
      : [],
  ),
  ...(route.redeliveryStops ?? []).map((stop) => ({
    id: stop.id,
    providerDelivery: stop.providerDelivery,
  })),
];

const statusOf = (stop: BurqStop): string | null =>
  stop.providerDelivery?.status ?? null;

export const isStopTerminal = (stop: BurqStop): boolean =>
  BURQ_TERMINAL_STATUSES.includes(statusOf(stop) ?? '');

export const isStopPastPickup = (stop: BurqStop): boolean =>
  BURQ_PAST_PICKUP_STATUSES.includes(statusOf(stop) ?? '');

const isFlagged = (stop: BurqStop): boolean =>
  Boolean(stop.providerDelivery?.needsAttention);

// Settled for the card (terminal, or held for a person in Burq). Polling keys
// on terminal only: a held delivery can still move (isBurqRouteMoving).
export const isStopSettled = (stop: BurqStop): boolean =>
  isStopTerminal(stop) || isFlagged(stop);

export type BurqCounts = {
  total: number;
  delivered: number;
  failed: number;
  returned: number;
  handledInBurq: number;
  unassigned: number;
  settled: number;
  anyPastPickup: boolean;
  anyCourier: boolean;
};

export const burqCounts = (route: Route): BurqCounts => {
  const stops = burqStopsOf(route);
  const withStatus = (status: string): number =>
    stops.filter((stop) => statusOf(stop) === status).length;
  // A terminal status outranks the held flag, which LDS leaves set on a stop
  // Burq later delivers.
  const heldOpen = stops.filter(
    (stop) => isFlagged(stop) && !isStopTerminal(stop),
  ).length;
  return {
    total: stops.length,
    delivered: withStatus('delivered'),
    failed: withStatus('failed'),
    returned: withStatus('returned'),
    handledInBurq: heldOpen,
    unassigned: heldOpen,
    settled: stops.filter(isStopSettled).length,
    anyPastPickup: stops.some(isStopPastPickup),
    anyCourier: stops.some((stop) =>
      Boolean(stop.providerDelivery?.courierAssigned),
    ),
  };
};

// null for a route with no card: not a Burq route, or a dispatch that failed
// or was unmarked (the route is a regular one again).
export const burqCardState = (route: Route): BurqCardState | null => {
  const status = route.providerDispatch?.status;
  if (!isBurqRoute(route) || !status) return null;
  if (status === GProviderDispatchStatus.Pushing) return BurqCardState.Sending;
  if (PRE_DISPATCH.includes(status)) return BurqCardState.CourierNotChosen;
  if (!DISPATCHED.includes(status)) return null;

  const counts = burqCounts(route);
  if (counts.total > 0 && counts.settled === counts.total)
    return counts.delivered === counts.total
      ? BurqCardState.Delivered
      : BurqCardState.Done;
  // A courier on the route outranks a pending cancel: Burq will refuse it.
  if (counts.anyPastPickup) return BurqCardState.OutForDelivery;
  if (counts.anyCourier) return BurqCardState.DriverAssigned;
  if (isCancelPending(route.providerDispatch)) return BurqCardState.Canceling;
  return BurqCardState.WaitingForDriver;
};

// The words for a Burq route's state, shared by its card, its panel's bar and
// its route header. Before dispatch they are the dispatch status's own label;
// once dispatched, the state its stops add up to.
export const burqStateLabel = (route: Route): string | null => {
  const state = burqCardState(route);
  const status = route.providerDispatch?.status;
  if (!state || !status) return null;
  return DISPATCHED.includes(status)
    ? state
    : providerDispatchStatusLabel(status);
};

// The card's second line once Done: the outcomes that were not deliveries.
export const burqProgressText = (route: Route): string | null => {
  if (burqCardState(route) !== BurqCardState.Done) return null;
  const counts = burqCounts(route);
  return [
    counts.failed ? `${counts.failed} failed` : null,
    counts.returned ? `${counts.returned} returned` : null,
    counts.handledInBurq ? `${counts.handledInBurq} handled in Burq` : null,
  ]
    .filter(Boolean)
    .join(', ');
};

export const isDispatchLocked = (route: Route): boolean => {
  const status = route.providerDispatch?.status;
  return isProviderRoute(route) && Boolean(status && LOCKED.includes(status));
};

// Whether a poll can still change what the card or panel shows: the route is
// being sent or quoted, or some stop has not reached a final outcome.
export const isBurqRouteMoving = (route: Route): boolean => {
  const status = route.providerDispatch?.status;
  if (!isBurqRoute(route) || !status) return false;
  if (
    status === GProviderDispatchStatus.Pushing ||
    PRE_DISPATCH.includes(status)
  )
    return true;
  if (!DISPATCHED.includes(status)) return false;
  const stops = burqStopsOf(route);
  return stops.length === 0 || !stops.every(isStopTerminal);
};

const minutesUntil = (iso: string, now: Date): number =>
  Math.round((Date.parse(iso) - now.getTime()) / 60_000);

// The no-courier alert has fired and nobody has picked the route up. The
// stamp is not cleared on assignment, so a courier turns the card back.
export const burqAmberText = (
  route: Route,
  now: Date = new Date(),
): string | null => {
  const dispatch = route.providerDispatch;
  if (!dispatch?.courierAlertSentAt || burqCounts(route).anyCourier)
    return null;
  if (!dispatch.pickupAt)
    return 'No driver yet · pickup ASAP · Check with Burq';
  const minutes = minutesUntil(dispatch.pickupAt, now);
  const when =
    minutes >= 0 ? `pickup in ${minutes} min` : `pickup ${-minutes} min ago`;
  return `No driver yet · ${when} · Check with Burq`;
};

export const formatProviderTime = (iso: string, timeZone?: string): string =>
  formatDateTo12Hour(new Date(iso), timeZone);

// The courier's name as the provider gave it, from the first stop that has
// one: a Burq route goes to a single courier.
export const burqCourierName = (route: Route): string | null =>
  burqStopsOf(route).find((stop) => stop.providerDelivery?.courierName)
    ?.providerDelivery?.courierName ?? null;

const WITH_COURIER: readonly BurqCardState[] = [
  BurqCardState.DriverAssigned,
  BurqCardState.OutForDelivery,
];

// "Uber · driver J. · 1/5 delivered". The network is the dispatched quote's,
// "Burq" until one is known; the driver shows while one has the route; the
// count once dispatched.
export const burqCardSubtitle = (route: Route): string => {
  const state = burqCardState(route);
  const status = route.providerDispatch?.status;
  const courier = burqCourierName(route);
  const counts = burqCounts(route);
  return [
    route.providerDispatch?.chosenQuote?.provider || 'Burq',
    state && WITH_COURIER.includes(state) && courier
      ? `driver ${courier}`
      : null,
    status && DISPATCHED.includes(status) && counts.total > 0
      ? `${counts.delivered}/${counts.total} delivered`
      : null,
  ]
    .filter(Boolean)
    .join(' · ');
};

// The panel bar's first line: "Driver assigned · J. (Uber)" once a courier
// has the route, else the route's state, with the network when known.
export const burqBarTitle = (route: Route): string | null => {
  const label = burqStateLabel(route);
  if (!label) return null;
  const network = route.providerDispatch?.chosenQuote?.provider;
  const courier = burqCourierName(route);
  const who =
    courier && network ? `${courier} (${network})` : courier || network;
  return [burqCounts(route).anyCourier ? 'Driver assigned' : label, who || null]
    .filter(Boolean)
    .join(' · ');
};

// Its second line: "1/5 delivered · Uber $84.00", each part left out when
// unknown: a dispatch made outside the DMP carries only the quote's id.
export const burqBarDetail = (route: Route): string => {
  const status = route.providerDispatch?.status;
  const quote = route.providerDispatch?.chosenQuote;
  const counts = burqCounts(route);
  const total = quote ? quoteTotalCents(quote) : null;
  const courier = [
    quote?.provider,
    total === null ? null : formatDollars(total),
  ]
    .filter(Boolean)
    .join(' ');
  return [
    status && DISPATCHED.includes(status) && counts.total > 0
      ? `${counts.delivered}/${counts.total} delivered`
      : null,
    courier || null,
  ]
    .filter(Boolean)
    .join(' · ');
};

export const sortBurqRoutes = (routes: Route[]): Route[] =>
  [...routes].sort((a, b) => {
    const amber =
      Number(Boolean(burqAmberText(b))) - Number(Boolean(burqAmberText(a)));
    return amber || naturalSort(a.name, b.name);
  });

// Statuses Burq sends before the courier collects; anything else not in the
// past-pickup list is unknown to the DMP and shown as Burq sent it.
const BEFORE_PICKUP: readonly string[] = [
  'request',
  'scheduled',
  'delivery_created',
  'driver_not_assigned',
  'driver_assigned',
  'enroute_pickup',
  'arrived_at_pickup',
];

// Past pickup but not heading to the stop: an ETA would mislead.
const RAW_PAST_PICKUP: readonly string[] = ['enroute_to_return', 'disputed'];

export type ProviderStopLabel = {
  text: string;
  tone: 'success' | 'error' | 'neutral';
  trackingUrl?: string;
  // Delivered with proof: the cell links to the stop's Proof section.
  hasProof: boolean;
  // Held for a person in Burq: shown as the row's flag, with its reason.
  flagged?: boolean;
  reason?: string;
};

// Progress for a stop with no outcome or flag: the ETA once the courier has
// the goods, or is assigned and Burq gave one; Waiting before that. A return,
// a dispute or a status the DMP does not know is shown as Burq sent it.
const progressText = (stop: BurqStop, timeZone?: string): string => {
  const row = stop.providerDelivery;
  const status = row?.status ?? null;
  const eta = row?.dropoffEta
    ? `ETA ${formatProviderTime(row.dropoffEta, timeZone)}`
    : null;
  const pastPickup = isStopPastPickup(stop);
  if (status && RAW_PAST_PICKUP.includes(status)) return status;
  if (status && !pastPickup && !BEFORE_PICKUP.includes(status)) return status;
  if (pastPickup) return eta ?? status ?? '';
  if (row?.courierAssigned && eta) return eta;
  return 'Waiting';
};

// A url Burq sent, kept only when it is http or https: LDS forwards the
// provider's strings as they came, and they become links and images here.
export const safeProviderUrl = (url?: string | null): string | undefined => {
  if (!url) return undefined;
  try {
    const { protocol } = new URL(url);
    return protocol === 'http:' || protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
};

export const safeProofUrls = (urls?: string[] | null): string[] =>
  (urls ?? []).filter((url) => safeProviderUrl(url) !== undefined);

// One stop's cell in the route panel: outcome first, then a flag, then
// progress.
export const providerStopLabel = (
  stop: BurqStop,
  dispatchStatus: GProviderDispatchStatus | undefined,
  timeZone?: string,
): ProviderStopLabel => {
  const row = stop.providerDelivery;
  const status = row?.status ?? null;
  const trackingUrl = safeProviderUrl(row?.trackingUrl);
  if (status === 'delivered')
    return {
      text: 'Delivered',
      tone: 'success',
      hasProof: safeProofUrls(row?.proofUrls).length > 0,
    };
  if (status === 'failed' || status === 'returned')
    return {
      text: status === 'failed' ? 'Failed' : 'Returned',
      tone: 'error',
      trackingUrl,
      hasProof: false,
    };
  if (row?.needsAttention)
    return {
      text: 'Unassigned in Burq',
      tone: 'error',
      trackingUrl,
      hasProof: false,
      flagged: true,
      reason: row.needsAttentionReason ?? undefined,
    };
  if (!row && dispatchStatus === GProviderDispatchStatus.Pushing)
    return { text: 'Sending', tone: 'neutral', hasProof: false };
  return {
    text: progressText(stop, timeZone),
    tone: 'neutral',
    trackingUrl,
    hasProof: false,
  };
};

// Whether a stop shows the held flag: by the label's own rule, so an outcome
// wins over a flag the provider never clears.
export const isProviderStopFlagged = (
  stop: BurqStop,
  dispatchStatus: GProviderDispatchStatus | undefined,
): boolean => Boolean(providerStopLabel(stop, dispatchStatus).flagged);

// LDS names a stop in a refusal by its database id ("stop 123: courier
// assigned ...", "redelivery stop 45: ..."). The GM knows it by name and
// number: "Cafe Name (stop 3): courier assigned ...". An id not on the route
// is left as LDS wrote it.
const STOP_ID_IN_MESSAGE = /\b(redelivery stop|stop) (\d+)(?=:)/g;

type NamedStop = {
  id: string;
  sequenceNumber?: string | null;
  location?: { businessName?: string } | null;
};

const namedStopsOf = (
  route: Pick<Route, 'stops' | 'redeliveryStops'> | null | undefined,
  kind: string,
): NamedStop[] =>
  kind === 'stop'
    ? (route?.stops ?? []).flatMap((stop) =>
        stop.__typename === 'Delivery' ? [stop] : [],
      )
    : route?.redeliveryStops ?? [];

export const nameStopsInMessage = (
  message: string,
  route: Pick<Route, 'stops' | 'redeliveryStops'> | null | undefined,
): string =>
  message.replace(STOP_ID_IN_MESSAGE, (match, kind: string, id: string) => {
    const stop = namedStopsOf(route, kind).find(
      (candidate) => candidate.id === id,
    );
    const name = stop?.location?.businessName;
    if (!stop || !name) return match;
    const position =
      stop.sequenceNumber === null || stop.sequenceNumber === undefined
        ? NaN
        : Number(stop.sequenceNumber) + 1;
    return Number.isNaN(position) ? name : `${name} (${kind} ${position})`;
  });
